// ==========================================================================
// Module registry (frontend)
//
// The backend loads modules from client.yaml; the frontend discovers them from
// the filesystem at build time. A module that ships a frontend/index.js is
// picked up with no edit to the shell: that is principle 1 (the core is
// untouchable) applied to the SPA.
//
// Each module's frontend/index.js exports:
//   routes      - vue-router route records
//   navigation  - sidebar entries [{ to, labelKey, permission, icon, group }]
//   messages    - optional i18n messages, merged per locale
// ==========================================================================

import { mergeMessages } from './mergeMessages'
import { moduleHelpKey, platformHelpKey } from './helpSource'

import * as authModule from '@general/auth/frontend'
import * as usersModule from '@general/users/frontend'
import * as companiesModule from '@general/companies/frontend'
import * as notificationsModule from '@general/notifications/frontend'
import * as settingsModule from '@general/settings/frontend'
import * as mailModule from '@general/mail/frontend'
import * as calendarModule from '@general/calendar/frontend'
import * as auditModule from '@general/audit/frontend'
import * as assistantModule from '@general/assistant/frontend'
import * as supportModule from '@general/support/frontend'
import * as demoModule from '@general/demo/frontend'

// Platform features come first and own their paths: a business module must not
// be able to shadow /login or /users.
const PLATFORM_FEATURES = [
  authModule, usersModule, companiesModule, notificationsModule,
  settingsModule, mailModule, calendarModule, auditModule, assistantModule, supportModule,
  // Contributes one public route, /demo, and no sidebar entry. On an instance
  // that is not a demo the route is simply never navigated to: the screen it
  // mounts asks the API for a catalogue, and there is none.
  demoModule,
]

// Eagerly evaluated at build time: Vite turns this into a static import list, so
// the bundle contains exactly the modules present in the repository.
const moduleEntries = import.meta.glob('../../../../modules/*/frontend/index.js', { eager: true })

const businessModules = Object.entries(moduleEntries).map(([path, module]) => ({
  name: path.match(/modules\/([^/]+)\//)?.[1] ?? 'unknown',
  ...module,
}))

export const features = [...PLATFORM_FEATURES, ...businessModules]

export const moduleRoutes = features.flatMap(feature => feature.routes ?? [])

/**
 * Lo que un módulo necesita tener montado en TODAS las pantallas, no en una.
 *
 * El caso que lo trajo es una llamada entrante: suena mientras la persona está
 * en una factura o en un parte, y la pantalla de la llamada no puede depender
 * de que esté abierta la del módulo que llama. Cada entrada:
 *
 *   { id, component, permission? }
 *
 * El armazón la monta solo con sesión, solo si la persona tiene el permiso y
 * solo si el módulo corre de verdad en esta instancia (`/health`): un
 * superadministrador tiene `*` y sin esa segunda guarda montaría la pieza de un
 * módulo que no existe. Marcada con su módulo igual que el menú, y por lo mismo:
 * el nombre sale de la carpeta y ningún módulo puede mentir sobre el suyo.
 */
export const moduleOverlays = features.flatMap(feature =>
  (feature.overlays ?? []).map(entry => ({ ...entry, module: feature.name })))

/**
 * Las entradas de menú de todo lo instalado, cada una marcada con de dónde
 * viene.
 *
 * La marca es lo que permite al menú agrupar por módulo (ver `inBands` en
 * navigationRules.js), y se pone AQUÍ porque este es el único sitio que lo
 * sabe: el nombre sale de la carpeta, no de nada que el módulo declare, así
 * que ningún módulo puede equivocarse ni mentir sobre el suyo.
 *
 * Las piezas de plataforma no tienen nombre de carpeta de módulo y salen sin
 * marcar, que es lo correcto: Inicio, Notificaciones y Soporte no son de
 * ningún módulo.
 */
export const moduleNavigation = features.flatMap(feature =>
  (feature.navigation ?? []).map(entry => ({ ...entry, module: feature.name })))

/**
 * Locale messages contributed by modules, merged branch by branch.
 *
 * Branch by branch and not by top-level key, because a module names itself
 * inside blocks the shell also owns: `modules.<module>` on the roles screen,
 * `permissions.<module>`, `settings.ns.<module>`, `audit.actions.<module>`.
 * That is what lets installing a module leave the platform's own screens
 * complete, with nothing to add to the shell by hand.
 */
export function moduleMessages(locale) {
  return features.reduce((merged, feature) => {
    const messages = feature.messages?.[locale]
    return messages ? mergeMessages(merged, messages) : merged
  }, {})
}

export { mergeMessages }

/**
 * La documentación que aporta cada módulo, para la sección de ayuda.
 *
 * ── Por qué la recoge el armazón y no la escribe ─────────────────────────────
 *
 * Quien sabe cómo funciona una función es el módulo que la tiene. Un manual
 * escrito en el armazón sería el armazón sabiendo qué módulos hay — justo lo que
 * el principio 1 evita — y se quedaría viejo a la tercera versión de cualquiera
 * de ellos.
 *
 * Así que el módulo declara sus temas y el armazón los ORDENA: una instancia sin
 * `work-orders` no tiene su documentación, igual que no tiene su menú, y nadie
 * ha tenido que tocar nada para que así sea.
 *
 * Cada tema: `{ id, titleKey, summaryKey, component }`. El `id` es la dirección
 * (`/help/work-orders.worksheets`), así que se puede enlazar desde el «?» de una
 * pantalla, marcar en el navegador y mandar por WhatsApp a quien empieza.
 *
 * ── Y la otra forma: un `.md` por idioma ─────────────────────────────────────
 *
 * Un tema declarado arriba es un componente, y sirve para lo que hay que
 * DIBUJAR: el parte por plantilla enseña sus ocho tipos de respuesta con el
 * aspecto que tienen en la pantalla de verdad.
 *
 * La explicación entera de un módulo no es eso: es texto. Y ese texto tiene que
 * salir en dos sitios —aquí dentro y en el manual que se le entrega al cliente,
 * que compila VitePress— así que vive en `modules/<módulo>/help/<idioma>.md` y
 * no en un componente: un componente Vue no se puede compilar a un PDF ni leer
 * en un repositorio.
 *
 * **Perezosos a propósito.** Sin `eager`, Vite deja cada `.md` en su propio
 * trozo y lo pide cuando alguien abre ESE tema. Con veinticinco módulos por
 * cuatro idiomas, cargarlos todos serían cientos de kilobytes en el arranque de
 * una cuadrilla que sólo quería fichar.
 *
 * Por eso el título y el resumen NO salen del `.md`: el índice los necesita
 * antes de abrir nada. Salen de `<módulo>.help.title` y `.subtitle`, que son
 * dos frases y viajan con el resto del i18n del módulo.
 */
const helpSources = import.meta.glob('../../../../modules/*/help/*.md', {
  query: '?raw',
  import: 'default',
})

const markdownHelp = (() => {
  const byModule = new Map()

  for (const [file, load] of Object.entries(helpSources)) {
    const found = moduleHelpKey(file)
    if (!found) continue
    const { id: name, locale } = found
    if (!byModule.has(name)) byModule.set(name, {})
    byModule.get(name)[locale] = load
  }

  return [...byModule.entries()].map(([name, sources]) => ({
    // El módulo ES la dirección: `/help/crm`. Un tema de componente lleva punto
    // (`work-orders.worksheets`) porque es UNA función del módulo; esto es el
    // módulo entero, y quien lo manda por WhatsApp escribe menos.
    id: name,
    module: name,
    titleKey: `${name}.help.title`,
    summaryKey: `${name}.help.subtitle`,
    sources,
  }))
})()

/**
 * Y lo que no es de ningún módulo: la plataforma.
 *
 * Roles, usuarios, ajustes, los avisos, la IA, cómo se abre un ticket. Son las
 * pantallas que están en TODAS las instancias, y hasta ahora eran las únicas del
 * ERP sin una línea escrita: un cliente con un problema encontraba explicado el
 * módulo que compró y no encontraba nada sobre quién ve qué o a quién avisar.
 *
 * Viven en `platform/help/<tema>/<idioma>.md` —fuera de cualquier paquete—
 * porque un tema no se corresponde con un paquete: «Buscar y moverse» cruza el
 * armazón entero y «Usuarios, contraseñas y doble factor» son `auth` y `users`
 * juntos. Agrupar por paquete obligaría a partir en dos lo que la persona vive
 * como una cosa.
 *
 * Van **sin módulo**, y eso es lo que hace que se enseñen siempre: `topicsFor`
 * sólo esconde los temas de un módulo que esta instancia no monta, y estos no
 * son de ninguno.
 */
const platformSources = import.meta.glob('../../../../platform/help/*/*.md', {
  query: '?raw',
  import: 'default',
})

const platformHelp = (() => {
  const byTopic = new Map()

  for (const [file, load] of Object.entries(platformSources)) {
    // Leída con `platformHelpKey` y no con un patrón escrito aquí: la clave que
    // devuelve Vite viene relativa a ESTE fichero y ya colapsada
    // (`../../../help/ai/es.md`, sin el `platform/`), y el patrón que lo
    // ignoraba dejó la lista vacía sin un solo error en ninguna parte.
    const found = platformHelpKey(file)
    if (!found) continue
    const { id, locale } = found
    if (!byTopic.has(id)) byTopic.set(id, {})
    byTopic.get(id)[locale] = load
  }

  return [...byTopic.entries()].map(([id, sources]) => ({
    id,
    // Sin módulo a propósito (ver arriba). El índice los agrupa bajo
    // `modules.platform`, que ya está traducido en los cuatro idiomas.
    module: null,
    titleKey: `help.topics.${id}.title`,
    summaryKey: `help.topics.${id}.subtitle`,
    sources,
  }))
})()

export const moduleHelp = [
  // Primero la explicación del módulo entero y después sus funciones sueltas:
  // quien llega al índice sin saber nada necesita la de arriba, y quien busca
  // una función concreta ya sabe lo que busca.
  ...markdownHelp,
  ...features.flatMap(feature => (feature.help ?? []).map(topic => ({ ...topic, module: feature.name }))),
  // La plataforma al final, por la misma razón que va abajo en el menú y en
  // Ajustes: lo que el cliente compró primero, nuestra fontanería después.
  ...platformHelp,
]

export const installedModules = businessModules.map(module => module.name)
